// ─── La pagina del collegamento arrivato per posta ───────────────────────────
//
// ⚖️ È l'UNICA schermata del gestionale che si apre con un segreto
// nell'indirizzo, e ci sono due conseguenze che si vedono nel codice qui sotto:
//   · il gettone si toglie dalla barra degli indirizzi appena è stato letto
//     (`replace`), perché la cronologia del browser resta, e resta anche sui
//     computer condivisi della segreteria;
//   · finita la riapertura NON si entra: si torna alla schermata di accesso e
//     si digita la password nuova. Così l'ultima cosa che succede è la prova
//     che quella password funziona davvero.

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DURATA_RIAPERTURA_MINUTI, LUNGHEZZA_MINIMA_PASSWORD } from '@pmo/shared';
import { riapriConGettone } from '../../lib/api.js';
import { CampoPassword, Guasto } from '../../components/pezzi.js';

export function Riapri(): React.JSX.Element {
  const [parametri] = useSearchParams();
  const vaiA = useNavigate();
  // Si legge UNA volta e si tiene: il `replace` qui sotto svuota la barra, e
  // senza questa copia la pagina si ritroverebbe senza gettone al primo
  // ridisegno.
  const [gettone] = useState(() => parametri.get('t') ?? '');

  const [nuova, setNuova] = useState('');
  const [ripeti, setRipeti] = useState('');
  const [fatto, setFatto] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<unknown>(null);

  useEffect(() => {
    // 🔒 Via dalla barra degli indirizzi. Non protegge da chi è già dentro il
    // computer, ma toglie il gettone dalla cronologia e da quello che finisce
    // in uno screenshot o in un `Referer`.
    if (parametri.get('t')) vaiA('/riapri', { replace: true });
  }, [parametri, vaiA]);

  const diverse = ripeti !== '' && ripeti !== nuova;

  async function manda(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErrore(null);
    if (diverse) return;
    setInCorso(true);
    try {
      await riapriConGettone(gettone, nuova);
      setFatto(true);
      setNuova('');
      setRipeti('');
    } catch (err) {
      setErrore(err);
    } finally {
      setInCorso(false);
    }
  }

  if (!gettone) {
    return (
      <main className="accesso">
        <section className="riquadro">
          <h1>Collegamento incompleto</h1>
          <p className="tenue">
            Questo indirizzo non porta nessun collegamento. Aprilo dal messaggio che hai ricevuto, oppure
            chiedine un altro dalla schermata di accesso.
          </p>
          <button type="button" onClick={() => vaiA('/')}>
            Torna all’accesso
          </button>
        </section>
      </main>
    );
  }

  if (fatto) {
    return (
      <main className="accesso">
        <section className="riquadro">
          <h1>Fatto</h1>
          {/* Il «fatto» compare DOPO la risposta del server, mai prima (§ 03.7). */}
          <p className="riuscito">
            La password è stata rifatta. Le sessioni aperte altrove sono state chiuse.
          </p>
          <p className="tenue piccolo">
            Adesso entra dalla schermata di accesso con la password nuova: è il modo di vedere subito che
            funziona.
          </p>
          <button type="button" onClick={() => vaiA('/')}>
            Vai all’accesso
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="accesso">
      <form className="riquadro" onSubmit={manda}>
        <h1>Scegli una password nuova</h1>
        <p className="sottotitolo">
          Questo collegamento vale {DURATA_RIAPERTURA_MINUTI} minuti e una volta sola.
        </p>

        <CampoPassword
          etichetta="Password nuova"
          autoComplete="new-password"
          required
          minLength={LUNGHEZZA_MINIMA_PASSWORD}
          value={nuova}
          onChange={(e) => setNuova(e.target.value)}
        />
        <CampoPassword
          etichetta="Ripetila"
          autoComplete="new-password"
          required
          value={ripeti}
          onChange={(e) => setRipeti(e.target.value)}
        />
        {diverse && <p className="guasto">Le due password non coincidono.</p>}
        <Guasto errore={errore} />

        <button type="submit" disabled={inCorso || diverse}>
          {inCorso ? 'Un attimo…' : 'Rifai la password'}
        </button>
        <button type="button" className="piatto" onClick={() => vaiA('/')}>
          Torna all’accesso
        </button>
      </form>
    </main>
  );
}
